import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, PencilSimple, QrCode, Power } from '@phosphor-icons/react';
import ExportButton from '../../components/ExportButton';
import SuperAdminDeleteButton from '../../components/SuperAdminDeleteButton';

const VEHICLE_EXPORT_COLUMNS = [
  { key: 'vehicle_number', label: 'Vehicle Number' },
  { key: 'vehicle_id_manual', label: 'Vehicle ID' },
  { key: 'body_type', label: 'Body Type' },
  { key: 'halt_location', label: 'Halt Location' },
  { key: 'fuel_type', label: 'Fuel Type' },
  { key: 'current_pilot_id', label: 'Current Pilot ID' },
  { key: 'status', label: 'Status', format: r => r.status || 'active' },
];

export default function VehicleManagement() {
  const [vehicles, setVehicles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrDialog, setQrDialog] = useState(null);
  const [form, setForm] = useState({ vehicle_number: '', vehicle_id_manual: '', body_type: '', halt_location: '', fuel_type: 'diesel' });

  useEffect(() => { fetchVehicles(); }, []);

  const fetchVehicles = async () => {
    try { const { data } = await api.get('/admin/vehicles'); setVehicles(data); } catch {}
  };

  const resetForm = () => { setForm({ vehicle_number: '', vehicle_id_manual: '', body_type: '', halt_location: '', fuel_type: 'diesel' }); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/vehicles/${editing.id}`, { vehicle_id_manual: form.vehicle_id_manual, body_type: form.body_type, halt_location: form.halt_location, fuel_type: form.fuel_type });
      } else {
        await api.post('/admin/vehicles', form);
      }
      fetchVehicles();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const handleEdit = (v) => {
    setEditing(v);
    setForm({ vehicle_number: v.vehicle_number, vehicle_id_manual: v.vehicle_id_manual || '', body_type: v.body_type || '', halt_location: v.halt_location || '', fuel_type: v.fuel_type || 'diesel' });
    setDialogOpen(true);
  };

  const handleToggle = async (id) => {
    await api.put(`/admin/toggle-status/vehicles/${id}`);
    fetchVehicles();
  };

  return (
    <div data-testid="vehicle-management" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Vehicles</h1>
          <p className="text-[#666] text-sm mt-1">{vehicles.length} registered vehicles</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton rows={vehicles} columns={VEHICLE_EXPORT_COLUMNS} filename="vehicles" testId="export-vehicles-btn" />
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-vehicle-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}>
              <Plus size={16} className="mr-2" /> Add Vehicle
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-bold tracking-tight">{editing ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Vehicle Number *</Label>
                <Input data-testid="vehicle-number-input" value={form.vehicle_number} onChange={e => setForm({...form, vehicle_number: e.target.value})} required className="rounded-none" placeholder="TN01AB1234" disabled={!!editing} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Vehicle ID</Label>
                <Input data-testid="vehicle-id-input" value={form.vehicle_id_manual} onChange={e => setForm({...form, vehicle_id_manual: e.target.value})} className="rounded-none" placeholder="e.g. V001, VEH-101" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Body Type</Label>
                <Input value={form.body_type} onChange={e => setForm({...form, body_type: e.target.value})} className="rounded-none" placeholder="e.g. Mini Truck, Tempo, etc." />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Halt Location</Label>
                <Input value={form.halt_location} onChange={e => setForm({...form, halt_location: e.target.value})} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Fuel Type</Label>
                <Input value={form.fuel_type} onChange={e => setForm({...form, fuel_type: e.target.value})} className="rounded-none" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Cancel</Button>
                <Button data-testid="vehicle-save-btn" type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* QR Dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>QR Code - {qrDialog?.vehicle_number}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {qrDialog?.qr_code && <img src={`data:image/png;base64,${qrDialog.qr_code}`} alt="QR Code" className="w-64 h-64" />}
            <p className="text-sm text-[#666]">Scan this QR code to assign this vehicle to a pilot</p>
            <Button onClick={() => {
              const link = document.createElement('a');
              link.href = `data:image/png;base64,${qrDialog?.qr_code}`;
              link.download = `${qrDialog?.vehicle_number}_qr.png`;
              link.click();
            }} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">
              Download QR
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Vehicle Number</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Vehicle ID</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Body Type</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Halt Location</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Fuel</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.map((v, i) => (
              <TableRow key={v.id} className={i % 2 === 0 ? '' : 'bg-[#F3F3F3]/30'}>
                <TableCell className="font-medium">{v.vehicle_number}</TableCell>
                <TableCell className="font-mono text-xs">{v.vehicle_id_manual || '-'}</TableCell>
                <TableCell>{v.body_type}</TableCell>
                <TableCell>{v.halt_location}</TableCell>
                <TableCell>{v.fuel_type}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${v.status === 'available' ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
                    {v.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button onClick={() => handleEdit(v)} className="p-2 hover:bg-[#F3F3F3] transition-colors" data-testid={`edit-vehicle-${v.id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => setQrDialog(v)} className="p-2 hover:bg-[#F3F3F3] transition-colors" data-testid={`qr-vehicle-${v.id}`}><QrCode size={16} /></button>
                  <button onClick={() => handleToggle(v.id)} className={`p-2 transition-colors ${v.status === 'inactive' ? 'hover:bg-[#00C853]/10 text-[#00C853]' : 'hover:bg-[#FF3333]/10 text-[#FF3333]'}`} title={v.status === 'inactive' ? 'Activate' : 'Deactivate'} data-testid={`toggle-vehicle-${v.id}`}><Power size={16} /></button>
                  <SuperAdminDeleteButton masterType="vehicles" recordId={v.id} recordName={v.vehicle_number} onDeleted={fetchVehicles} />
                </TableCell>
              </TableRow>
            ))}
            {vehicles.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-[#666] py-8">No vehicles registered</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
